package com.shadowings.gero.presentation.component.quiz

import androidx.compose.runtime.saveable.Saver

enum class MultiPressColumn { LEFT, RIGHT }

enum class MultiPressPairResult { CORRECT, WRONG }

data class MultiPressSelectionState(
    val selectedLeft: Int? = null,
    val selectedRight: Int? = null,
    val matchedLeft: Set<Int> = emptySet(),
    val matchedRight: Set<Int> = emptySet(),
    val wrongPair: Pair<Int, Int>? = null,
    val pairResult: MultiPressPairResult? = null,
    val shouldConfirm: Boolean = false,
)

// Only matchedLeft/matchedRight need to survive a configuration change (e.g. device rotation) —
// otherwise progress on already-matched pairs is lost and the user has to re-match everything.
// The rest (selectedLeft/Right, wrongPair, pairResult, shouldConfirm) is transient tap-in-progress
// state that's safe to reset to its default on restore.
val MultiPressSelectionStateSaver = Saver<MultiPressSelectionState, List<Int>>(
    save = { it.matchedLeft.toList() + it.matchedRight.toList() + it.matchedLeft.size },
    restore = { saved ->
        val splitIndex = saved.last()
        MultiPressSelectionState(
            matchedLeft = saved.subList(0, splitIndex).toSet(),
            matchedRight = saved.subList(splitIndex, saved.size - 1).toSet(),
        )
    }
)

internal fun onMultiPressTap(
    state: MultiPressSelectionState,
    column: MultiPressColumn,
    index: Int,
    rightToLeftMap: List<Int>,
    totalPairs: Int,
): MultiPressSelectionState {
    if (index !in 0 until rightToLeftMap.size) return state.copy(pairResult = null, shouldConfirm = false)
    if (state.wrongPair != null) return state.copy(pairResult = null, shouldConfirm = false)

    val normalized = state.copy(pairResult = null, shouldConfirm = false)

    return when (column) {
        MultiPressColumn.LEFT -> {
            if (index in normalized.matchedLeft) return normalized
            val right = normalized.selectedRight
            if (right == null) {
                normalized.copy(selectedLeft = index)
            } else {
                evaluatePair(normalized, left = index, right = right, rightToLeftMap, totalPairs)
            }
        }

        MultiPressColumn.RIGHT -> {
            if (index in normalized.matchedRight) return normalized
            val left = normalized.selectedLeft
            if (left == null) {
                normalized.copy(selectedRight = index)
            } else {
                evaluatePair(normalized, left = left, right = index, rightToLeftMap, totalPairs)
            }
        }
    }
}

internal fun clearMultiPressWrongPair(
    state: MultiPressSelectionState,
): MultiPressSelectionState {
    return state.copy(
        selectedLeft = null,
        selectedRight = null,
        wrongPair = null,
        pairResult = null,
        shouldConfirm = false,
    )
}

private fun evaluatePair(
    state: MultiPressSelectionState,
    left: Int,
    right: Int,
    rightToLeftMap: List<Int>,
    totalPairs: Int,
): MultiPressSelectionState {
    val isCorrect = rightToLeftMap[right] == left
    if (!isCorrect) {
        return state.copy(
            selectedLeft = left,
            selectedRight = right,
            wrongPair = left to right,
            pairResult = MultiPressPairResult.WRONG,
        )
    }

    val nextMatchedLeft = state.matchedLeft + left
    val nextMatchedRight = state.matchedRight + right
    val completed = nextMatchedLeft.size == totalPairs

    return state.copy(
        selectedLeft = null,
        selectedRight = null,
        matchedLeft = nextMatchedLeft,
        matchedRight = nextMatchedRight,
        wrongPair = null,
        pairResult = MultiPressPairResult.CORRECT,
        shouldConfirm = completed,
    )
}

